import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';

export const userKeys = {
  all: ['users'],
  lists: () => [...userKeys.all, 'list'],
  list: (orgId) => [...userKeys.lists(), orgId],
  details: () => [...userKeys.all, 'detail'],
  detail: (id) => [...userKeys.details(), id],
};

// Fetch all users (optionally filtered by orgId)
export function useUsers(orgId) {
  return useQuery({
    queryKey: orgId ? userKeys.list(orgId) : userKeys.all,
    queryFn: () => {
      const options = orgId ? { orgId } : {};
      return apiHelper.getResource('/auth/users/', options);
    },
    enabled: true,
  });
}

// Fetch single user
export function useUser(id, orgId) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => apiHelper.getResource(`/auth/users/${id}/`, { orgId }),
    enabled: !!id,
  });
}

// Create user
export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, orgId }) => apiHelper.postResource('/auth/users/', data, { orgId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

// Update user
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data, orgId }) => apiHelper.putResource(`/auth/users/${id}/`, data, { orgId }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
    },
  });
}

// Delete user
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orgId, confirm }) =>
      apiHelper.deleteResource(`/auth/users/${id}/`, {
        orgId,
        ...(confirm && { params: { confirm: 'yes' } })
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}
